import GlobalClient from "../hermesclient/client";
import { ContentType, RpcFrameType } from "../hermesclient/proto/wsmessages";
import { nameMappingService } from "./NameMappingService";
import { asLogicalMailbox, MailboxAddress } from "../hermesclient/types";

/**
 * archive.v1 RPC client (main thread).
 *
 * The log-viewer's stream worker cannot own the authenticated WS client, so
 * it relays archive.v1 control calls (ListArchives / OpenArchive) here via
 * the archive_rpc_request/response worker messages. The calls MUST ride the
 * user's own WS session: RPC-layer authorization is by sender mailbox, so an
 * HTTP bridge with a shared mailbox would not carry the user's roles.
 *
 * Bytes never travel this path — OpenArchive returns a blob handle and the
 * worker fetches the capability URL directly over HTTP.
 */
class ArchiveRpcClient {
  private targetMailbox: MailboxAddress | null = null;

  private async getTargetMailbox(): Promise<MailboxAddress> {
    if (!this.targetMailbox) {
      // The log-archiver engine rides the same mesh process the registry
      // name maps to (same resolution the a8 CLI uses for archive.v1).
      this.targetMailbox = await nameMappingService.resolve(
        asLogicalMailbox("registry"),
      );
    }
    return this.targetMailbox;
  }

  async call(
    endPoint: string,
    request: Record<string, unknown>,
  ): Promise<unknown> {
    const client = GlobalClient.get();
    const targetMailbox = await this.getTargetMailbox();
    const requestBytes = new TextEncoder().encode(JSON.stringify(request));

    const rpcResponse = await client.rawRpcCall({
      to: targetMailbox,
      endPoint,
      body: requestBytes,
      contentType: ContentType.Json,
    });

    const frameType = rpcResponse.header?.rpcHeader?.frameType;
    if (frameType === RpcFrameType.ErrorResponse) {
      const errorInfo = rpcResponse.header?.rpcHeader?.errorInfo;
      const errorMessage =
        errorInfo?.message ||
        errorInfo?.errorNode?.frame?.message ||
        `RPC call to ${endPoint} failed`;
      throw new Error(errorMessage);
    }

    if (!rpcResponse.data || rpcResponse.data.length === 0) {
      return {};
    }

    const responseText = new TextDecoder().decode(rpcResponse.data);
    return JSON.parse(responseText);
  }
}

export const archiveRpcClient = new ArchiveRpcClient();
